import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  FunnelChart,
  Funnel,
  LabelList,
} from "recharts";
import {
  getLeadsPerSalesRep,
  getClientsContacted,
  getBudgetSummary,
  getTopServices,
  getSalesFunnel,
  getTotalLeads,
  getDealsByStatusLevel,
  getDeliveredLogs,
  getUndeliveredLogs,
} from "../../../api/reporting";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#8dd1e1"];
const formatName = (item) => {
  if (!item) return "Unknown";
  // For sales rep data
  if (item.salesRepName) return item.salesRepName;
  if (item.adminName) return item.adminName;
  // For service data
  if (item.serviceName) return item.serviceName;
  if (item.name) return item.name;
  // For status data
  if (item.statusName) return item.statusName;
  if (item.title) return item.title;
  // For client/lead data
  if (item.clientName) return item.clientName;
  if (item.leadName) return item.leadName;
  // For dates, format them nicely
  if (item.date) {
    try {
      return new Date(item.date).toLocaleDateString();
    } catch (e) {
      return item.date;
    }
  }
  // If we have a nested name object
  if (item._id && typeof item._id === "object") {
    const idObj = item._id;
    return idObj.name || idObj.title || idObj.fullName || "Unknown";
  }
  // For category data
  if (item.category) return item.category;
  // If nothing else matches, try to make the ID readable
  if (item._id) return String(item._id);
  return "Unknown";
};

const ChartCard = ({ title, children }) => (
  <div
    style={{
      background: "white",
      padding: "20px",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      height: "400px",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <h2 style={{ marginBottom: "20px", fontSize: "18px", fontWeight: "bold" }}>
      {title}
    </h2>
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {children}
    </div>
  </div>
);

const GraphPage = () => {
  const [leadsData, setLeadsData] = useState([]);
  const [contactsData, setContactsData] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [crmFunnelData, setCrmFunnelData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem("currentUser")) || {};
  const role = currentUser.role || currentUser.roleName || "";
  const hasPermission = (action) => {
    if (role.name === "super admin") return true;
    return (
      role.permissions &&
      role.permissions.some(
        (perm) =>
          perm.resource === "Management" && perm.actions.includes(action)
      )
    );
  };

  const canRead = () => hasPermission("read");
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const pagination = { page: 1, limit: 100 };
        const payload = {};
        const [
          leadsRes,
          contactsRes,
          budgetRes,
          servicesRes,
          funnelRes,
          totalLeadsRes,
          dealsStatusRes,
          deliveredRes,
          undeliveredRes,
        ] = await Promise.all([
          getLeadsPerSalesRep(payload, pagination),
          getClientsContacted(payload, pagination),
          getBudgetSummary(payload, pagination),
          getTopServices(payload, pagination),
          getSalesFunnel(payload, pagination),
          getTotalLeads(payload, pagination),
          getDealsByStatusLevel(payload, pagination),
          getDeliveredLogs(payload, pagination),
          getUndeliveredLogs(payload, pagination),
        ]);

        setLeadsData(
          leadsRes.data?.map((item) => ({
            name: formatName(item),
            value: item.totalLeads || item.count || 0,
          })) || []
        );

        const total = contactsRes.data?.length || 0;
        setContactsData([{ name: "Total Contacts", value: total }]);
        console.log("11111111111111111111", total);

        setBudgetData(
          budgetRes.data?.map((item) => ({
            name: formatName(item),
            value: item.totalBudget || 0,
          })) || []
        );

        setServicesData(
          servicesRes.data?.map((item) => ({
            name: item._id || "Unknown Service",
            value: item.usageCount || item.count || 0,
          })) || []
        );

        setFunnelData(
          funnelRes.data?.map((item) => ({
            name: formatName(item),
            value: item.dealCount || 0,
          })) || []
        );

        const totalLeads = totalLeadsRes.data[0]?.totalLeads || 0;
        const connectedLeads = contactsRes.data?.length;

        // Initialize variables
        let intialDeal = 0;
        let canceledDeals = 0;
        let finalDeals = 0;

        // Loop through the data to find deals based on their level
        if (dealsStatusRes?.data && Array.isArray(dealsStatusRes.data)) {
          dealsStatusRes.data.forEach((deal) => {
            if (deal?.level === "start") {
              intialDeal = deal.totalDealsInLevel || 0;
            } else if (deal?.level === "failed") {
              canceledDeals = deal.totalDealsInLevel || 0;
            } else if (deal?.level === "final") {
              finalDeals = deal.totalDealsInLevel || 0;
            }
          });
        }

        console.log('Number of "start" levels:', intialDeal);
        const delivered = deliveredRes.data?.logs.length || 0;
        const notDelivered = undeliveredRes.data?.logs.length || 0;

        console.log("connectedLeadsconnectedLeads888888888", dealsStatusRes);
        const crmData = [
          {
            name: "Total Leads",
            value: totalLeads || 0,
            fill: "#7B68EE", // Medium slate blue
          },
          {
            name: "Contact Leads",
            value: connectedLeads || 0,
            fill: "#6495ED", // Cornflower blue
          },
          {
            name: "Initiated Deals",
            value: intialDeal || 0,
            fill: "#4F94CD", // Steel blue
          },
          {
            name: "Canceled Deals",
            value: canceledDeals || 0,
            fill: "#4682B4", // Steel blue
          },
          {
            name: "Final Deals",
            value: finalDeals || 0,
            fill: "#4169E1", // Royal blue
          },
          {
            name: "Delivered",
            value: delivered || 0,
            fill: "#1E90FF", // Dodger blue
          },
          {
            name: "Not Delivered",
            value: notDelivered || 0,
            fill: "#00BFFF", // Deep sky blue
          },
        ];

        // Filter out zero values and ensure proper funnel visualization
        const processedCrmData = crmData.filter((item) => item.value > 0);

        setCrmFunnelData(processedCrmData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading reports...
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <h1
        style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "24px" }}
      >
        Reports Dashboard
      </h1>

      {error && (
        <div
          style={{
            padding: "12px",
            marginBottom: "20px",
            backgroundColor: "#fee2e2",
            border: "1px solid #ef4444",
            borderRadius: "4px",
            color: "#dc2626",
          }}
        >
          Error loading some data. The dashboard may be incomplete.
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(500px, 1fr))",
          gap: "24px",
        }}
      >
        {canRead() && (
          <>
            <ChartCard title="Leads per Sales Representative">
              {leadsData.length === 0 ? (
                <p>No data available to display.</p>
              ) : (
                <PieChart width={450} height={300}>
                  <Pie
                    data={leadsData}
                    cx={225}
                    cy={150}
                    labelLine={true}
                    label={(entry) => `${entry.name} (${entry.value})`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                  >
                    {leadsData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${value} leads`, "Total Leads"]}
                  />
                  <Legend formatter={(value) => value} />
                </PieChart>
              )}
            </ChartCard>

            <ChartCard title="Clients Contacted">
              {console.log("contactsData", contactsData)}
              {contactsData.length === 0 ||
              contactsData.every((item) => item.value === 0) ? (
                <p>No data available to display.</p>
              ) : (
                <PieChart width={450} height={300}>
                  <Pie
                    data={contactsData}
                    cx={225}
                    cy={150}
                    labelLine={true}
                    label={(entry) => `${entry.name}`}
                    outerRadius={100}
                    fill="#82ca9d"
                    dataKey="value"
                    nameKey="name"
                  >
                    {contactsData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [
                      `${value} contacts`,
                      "Total Contacts",
                    ]}
                  />
                  <Legend formatter={(value) => value} />
                </PieChart>
              )}
            </ChartCard>

            <ChartCard title="Budget Summary">
              {budgetData.length === 0 ? (
                <p>No data available to display.</p>
              ) : (
                <LineChart
                  width={450}
                  height={300}
                  data={budgetData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#ff8042"
                    name="Budget Amount"
                  />
                </LineChart>
              )}
            </ChartCard>

            <ChartCard title="Top Services">
              {servicesData.length === 0 ? (
                <p>No data available to display.</p>
              ) : (
                <PieChart width={450} height={300}>
                  <Pie
                    data={servicesData}
                    cx={225}
                    cy={150}
                    labelLine={true}
                    label={(entry) => `${entry.name}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                  >
                    {servicesData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value} uses`, name]}
                  />
                  <Legend formatter={(value) => value} />
                </PieChart>
              )}
            </ChartCard>

            <ChartCard title="CRM Info">
              {crmFunnelData.length === 0 ? (
                <p>No data available to display.</p>
              ) : (
                <FunnelChart width={450} height={300}>
                  <Tooltip />
                  <Funnel
                    dataKey="value"
                    data={crmFunnelData}
                    isAnimationActive
                    width={400}
                    neckWidth="30%"
                    neckHeight="0%"
                  >
                    <LabelList
                      position="right"
                      fill="#000"
                      stroke="none"
                      dataKey="name"
                      fontSize={12}
                    />
                  </Funnel>
                </FunnelChart>
              )}
            </ChartCard>

            {/* <ResponsiveContainer width="100%" height={400}>
              <FunnelChart>
                <Tooltip />
                <Funnel
                  dataKey="value"
                  data={crmFunnelData}
                  isAnimationActive
                  fill="#3399CC"
                >
                  <LabelList
                    dataKey="name"
                    position="left"
                    stroke="none"
                    fill="#333"
                  />
                  <LabelList
                    dataKey="value"
                    position="center"
                    stroke="none"
                    fill="#fff"
                    formatter={(val) => val >= 1000 ? `${(val / 1000).toFixed(2)}K` : val}
                  />
                  <LabelList
                    position="right"
                    stroke="none"
                    fill="#333"
                    dataKey="value"
                    formatter={(val) =>
                      `${((val / crmFunnelData[0].value) * 100).toFixed(2)}%`
                    }
                  />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer> */}
          </>
        )}
      </div>
    </div>
  );
};

export default GraphPage;
